import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { Map } from 'immutable';
import { isEqual, isUndefined, noop } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { Account } from '../../../account-data/account.model';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { DocumentTypesEnum } from '../../../document-data/document-data.data';
import { IDocumentStore } from '../../../document-data/document-store.interface';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { DocumentPayments } from '../../../document-data/sub-models/document-payments.model';
import { DocumentPreview } from '../../../document-data/sub-models/document-preview.model';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { States } from '../../../document-data/sub-models/states.model';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { InvoiceDataDispatcher } from '../../../invoice-data/invoice-data.dispatcher';
import { InvoiceDataStore } from '../../../invoice-data/invoice-data.store';
import { I2gMoney } from '../../../settings-data/i2g-money.data';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { PaymentReminders } from '../../../settings-data/sub-models/company-settings/payment-reminders.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { DocumentPreviewBuilder } from './document-preview.builder';

@Component({
  selector: 'app-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentPreviewBuilder],
})
export class DocumentPreviewComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() showPaid: boolean;
  @Input() useOutstandingBalance: boolean;
  @Input() showTotal: boolean;
  @Input() enablePayments: boolean;
  @Input() enableBankTransfer: boolean;
  @Input() enableCardPayments: boolean;
  @Input() enablePaypal: boolean;
  @Input() enableSchedules: boolean;
  @Input() enableDeposits: boolean;
  @Input() showPaymentActions: boolean;
  @Input() showPaymentReminders: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() depositRequestFeatureName: string;
  @Input() featureToggles: Toggles;
  @Input() featurePayloads: Map<string, any>;
  @Input() account: Account;

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  document$: Observable<Document>;
  documentHasUnsavedChanges$: Observable<boolean>;
  documentBilling$: Observable<Billing>;
  documentCalculationResult$: Observable<CalculationResult>;
  documentDate$: Observable<string>;
  documentDueDate$: Observable<string>;
  documentPaymentUrl$: Observable<string>;
  documentNumber$: Observable<string>;
  documentOverallState$: Observable<string>;
  documentPayments$: Observable<DocumentPayments>;
  documentSchedule$: Observable<Schedule>;
  paymentsSettings$: Observable<PaymentsSettings>;
  underwritingUrl$: Observable<string>;
  companySettings$: Observable<CompanySettings>;
  documentState$: Observable<States>;
  documentRevisionId$: Observable<string>;
  documentTrackingObject$: Observable<ITrackingObject>;
  sourceDocumentPreview$: Observable<DocumentPreview>;
  linkedDocumentPreview$: Observable<DocumentPreview>;
  documentType$: Observable<DocumentTypesEnum>;
  documentFeatureName$: Observable<string>;
  remindersSettings$: Observable<PaymentReminders>;
  deposit$: Observable<Deposit>;
  addDepositForFuture$: Observable<DepositToggleValue>;
  documentSettings$: Observable<DocumentPresetSettings>;
  documentId$: Observable<string>;
  showI2gMoney: boolean;
  i2gMoneyEnabled$: Observable<boolean>;
  showBankTransfer$: Observable<boolean>;
  showCardPayments$: Observable<boolean>;
  showPaypalEc$: Observable<boolean>;
  paypalEcStatus$: Observable<PayPalEcStatesEnum>;
  bankTransferEnabled$: Observable<boolean>;
  cardPaymentsEnabled$: Observable<boolean>;
  paypalEcEnabled$: Observable<boolean>;
  isAccountNotified$: Observable<boolean>;

  constructor(
    private _documentPreviewBuilder: DocumentPreviewBuilder,
    private _settingsDataStore: SettingsDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _utilsService: UtilsService,
    private _featuresDataStore: FeaturesDataStore,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    const validDocument$: Observable<Document> = this.documentDataStore.getValidEditingEntity();

    this.documentSchedule$ =
      this.documentDataStore instanceof InvoiceDataStore
        ? validDocument$.pipe(
            flatMap((document: Document) =>
              (this
                .documentDataStore as InvoiceDataStore).getSchedulesByInvoiceId(
                document.id
              )
            ),
            distinctUntilChanged((a, b) => a && a.equals(b))
          )
        : of(null);

    this.isAccountNotified$ = this._featuresDataStore.isAccountNotified$;

    this.documentHasUnsavedChanges$ = combineLatest([
      this.documentDataStore.hasEditingEntityValueChanged(),
      this.documentDataStore.hasEditingEntityBeenSaved(),
    ]).pipe(
      map(
        ([hasChanges, hasBeenSaved]: boolean[]) => hasChanges && !hasBeenSaved
      )
    );

    this.document$ = validDocument$;

    this.documentId$ = validDocument$.pipe(
      map((doc: Document) => doc.id),
      take(1)
    );

    this.documentType$ = validDocument$.pipe(
      map((doc: Document) => doc.documentType),
      take(1),
      shareReplay(1)
    );

    this.documentFeatureName$ = this.documentType$.pipe(
      map((documentType: DocumentTypesEnum) => {
        switch (documentType) {
          case DocumentTypesEnum.invoice:
            return Toggles.KEYS.INVOICES;
          case DocumentTypesEnum.estimate:
            return Toggles.KEYS.ESTIMATE;
          case DocumentTypesEnum.credit_memo:
            return Toggles.KEYS.CREDIT_MEMO;
          case DocumentTypesEnum.purchase_order:
            return Toggles.KEYS.PURCHASE_ORDER;
          default:
            return null;
        }
      }),
      filter((x) => !!x),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.documentSettings$ = validDocument$.pipe(
      map((document: Document) => document.settings),
      distinctUntilChanged((a, b) => a && a.equals(b)),
      shareReplay(1)
    );

    this.documentState$ = validDocument$.pipe(
      map((doc: Document) => doc.states),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentNumber$ = validDocument$.pipe(
      map((doc: Document) => doc.docNumber),
      distinctUntilChanged()
    );

    this.documentOverallState$ = validDocument$.pipe(
      map((doc: Document) => doc.overallState),
      distinctUntilChanged()
    );

    this.documentCalculationResult$ = validDocument$.pipe(
      map((doc: Document) => doc.calculationResult),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentPayments$ = validDocument$.pipe(
      map((doc: Document) => doc.payments),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentBilling$ = validDocument$.pipe(
      map((doc: Document) => doc.billing),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentDate$ = validDocument$.pipe(
      map((doc: Document) => doc.docDate),
      distinctUntilChanged()
    );

    this.documentDueDate$ = validDocument$.pipe(
      map((doc: Document) => doc.dueDate),
      distinctUntilChanged()
    );

    this.underwritingUrl$ = this._settingsDataStore.getUnderwritingUrl();

    this.companySettings$ = this._settingsDataStore.settings$.pipe(
      map((settings: Settings) => settings.companySettings),
      shareReplay(1)
    );

    this.paymentsSettings$ = this.companySettings$.pipe(
      map((companySettings: CompanySettings) => companySettings.payments),
      shareReplay(1)
    );

    this.documentPaymentUrl$ = combineLatest([
      validDocument$,
      this.paymentsSettings$,
    ]).pipe(
      map(([document, paymentsSettings]: [Document, PaymentsSettings]) => {
        return paymentsSettings.cardPaymentsEnabled() &&
          !document.settings.cardPaymentsDisabled
          ? document.links.electronicPaymentUrl
          : null;
      }),
      distinctUntilChanged()
    );

    this.showI2gMoney = this.featureToggles.isAllAccess(
      Toggles.KEYS.INVOICE2GO_MONEY
    );

    this.showBankTransfer$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
      this.documentFeatureName$,
    ]).pipe(
      map(
        ([documentSettings, paymentsSettings, documentFeatureName]: [
          DocumentPresetSettings,
          PaymentsSettings,
          string
        ]) =>
          this.enablePayments &&
          this.enableBankTransfer &&
          this.featureToggles.isAllAccess(documentFeatureName) &&
          !paymentsSettings.achDebit.isIneligible() &&
          paymentsSettings.achDebit.currencyCode ===
            documentSettings.currencyCode
      ),
      distinctUntilChanged()
    );

    const paymentsSettingsDocumentFeatureName$ = combineLatest([
      this.paymentsSettings$,
      this.documentFeatureName$,
    ]).pipe(shareReplay(1));

    this.showCardPayments$ = paymentsSettingsDocumentFeatureName$.pipe(
      map(
        ([paymentsSettings, documentFeatureName]: [
          PaymentsSettings,
          string
        ]) => {
          const stripe = paymentsSettings.cardPayments.stripe;

          return (
            this.enablePayments &&
            this.featureToggles.isAllAccess(documentFeatureName) &&
            !(stripe && stripe.isIneligible()) &&
            (this.enableCardPayments ||
              (this.featureToggles.isForbidden(Toggles.KEYS.CARD_PAYMENTS) &&
                stripe.isEnabled()))
          );
        }
      ),
      distinctUntilChanged()
    );

    this.showPaypalEc$ = paymentsSettingsDocumentFeatureName$.pipe(
      map(
        ([paymentsSettings, documentFeatureName]: [PaymentsSettings, string]) =>
          this.enablePayments &&
          this.featureToggles.isAllAccess(documentFeatureName) &&
          this.enablePaypal
      ),
      distinctUntilChanged()
    );

    this.paypalEcStatus$ = this.paymentsSettings$.pipe(
      map((paymentSettings) => paymentSettings.paypalEcStatus)
    );

    this.i2gMoneyEnabled$ = combineLatest([
      this.documentSettings$,
      this._settingsDataStore.i2gMoney$,
    ]).pipe(
      map(
        ([documentSettings, i2gMoney]: [DocumentPresetSettings, I2gMoney]) =>
          !documentSettings.bankTransferDisabled &&
          i2gMoney?.context?.settings.allowed_by_partner &&
          i2gMoney?.context?.settings.enabled_by_user
      ),
      distinctUntilChanged()
    );

    this.bankTransferEnabled$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
    ]).pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.bankTransferDisabled &&
          paymentsSettings.bankTransferEnabled()
      ),
      distinctUntilChanged()
    );

    const documentPaymentsSettings$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
    ]).pipe(shareReplay(1));

    this.cardPaymentsEnabled$ = documentPaymentsSettings$.pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.cardPaymentsDisabled &&
          paymentsSettings.cardPaymentsEnabled()
      ),
      distinctUntilChanged()
    );

    this.paypalEcEnabled$ = documentPaymentsSettings$.pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.paypalEcDisabled && paymentsSettings.paypalEcEnabled
      ),
      distinctUntilChanged()
    );

    this.documentRevisionId$ = validDocument$.pipe(
      map((doc: Document) => doc.revisionId),
      distinctUntilChanged()
    );

    this.sourceDocumentPreview$ = validDocument$.pipe(
      filter((doc: Document) => doc.documentType === DocumentTypesEnum.invoice),
      map((doc: Document) => this._utilsService.getConvertedDoc(doc)),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.linkedDocumentPreview$ = validDocument$.pipe(
      filter(
        (doc: Document) => doc.documentType === DocumentTypesEnum.estimate
      ),
      map((doc: Document) => this._utilsService.getConvertedDoc(doc)),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentTrackingObject$ = validDocument$.pipe(
      map((doc: Document) =>
        this._trackingObjectBuilder.buildDocument(
          doc.id,
          doc.localId,
          doc.docNumber,
          doc.overallState,
          doc.revisionId,
          doc.documentType
        )
      ),
      distinctUntilChanged((a, b) => isEqual(a, b))
    );

    this.remindersSettings$ = this.companySettings$.pipe(
      map(
        (companySettings: CompanySettings) => companySettings.paymentReminders
      ),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.deposit$ = validDocument$.pipe(
      map((doc: Document) => doc.deposit),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.addDepositForFuture$ = this.companySettings$.pipe(
      distinctUntilChanged((a, b) => a && a.equals(b)),
      withLatestFrom(this.documentType$),
      map(
        ([companySettings, documentType]: [
          CompanySettings,
          DocumentTypesEnum
        ]) => {
          const previousValue = companySettings.depositToggle[documentType];

          if (previousValue) {
            return previousValue;
          } else {
            const depositToggleValueKeys = DepositToggleValue.KEYS;
            const depositTogglePayload =
              this.featurePayloads &&
              this.featurePayloads.get(Toggles.KEYS.DEPOSIT_TOGGLE);
            const documentToggleValue =
              depositTogglePayload && depositTogglePayload.get(documentType);

            return !isUndefined(documentToggleValue)
              ? new DepositToggleValue({
                  [depositToggleValueKeys.VALUE]: 50,
                  [depositToggleValueKeys.TOGGLE_STATE]: documentToggleValue,
                })
              : null;
          }
        }
      ),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  }

  ngAfterViewInit() {
    this._trackingDispatcher.trackPage(this.trackingScreenName);
  }

  updateDocumentContent(documentContentProps: Map<string, any>) {
    this.documentDataDispatcher.setEditingEntityContent(documentContentProps);
  }

  updateSettingsContent(settingsContentProps: Map<string, any>) {
    this._settingsDataDispatcher.updateSettings(settingsContentProps);
    this._settingsDataDispatcher.saveCurrentSettingsToNetwork();
  }

  onUpdateRecurringSchedule(payload: Map<string, any>) {
    if (this.documentDataDispatcher instanceof InvoiceDataDispatcher) {
      const invoiceDataDispatcher = this
        .documentDataDispatcher as InvoiceDataDispatcher;

      this.documentId$
        .pipe(takeUntil(this._destroyed))
        .subscribe((documentId: string) => {
          if (payload) {
            invoiceDataDispatcher.updateSchedule(documentId, payload);
          } else {
            invoiceDataDispatcher.deleteSchedule(documentId);
          }
        });
    }
  }

  setInTmpCache() {
    this.documentDataStore
      .getValidEditingEntity()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe((editingDocument: Document) => {
        this.documentDataDispatcher.setInTmpCache(
          editingDocument.id,
          editingDocument
        );
      });
  }

  removeSignatures() {
    this.documentDataDispatcher
      .setEditingEntityContent(
        this._documentPreviewBuilder.buildDocumentContentWithoutSignatures()
      )
      .pipe(
        flatMap(() => this.documentDataDispatcher.saveEditingEntity()), // save document so that server has new pdf
        delay(100),
        takeUntil(this._destroyed)
      )
      .subscribe(noop, noop);
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
